import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  ChevronDown, ChevronRight, Calendar, Building2,
  TrendingDown, Zap, FileText, Upload, MessageSquare, RefreshCw, Check,
} from 'lucide-react';
import { authHeaders } from '../../utils/authHeaders';
import { BASE_URL } from '../../components/constants';
import { useToast } from '../../components/Toast';
import { fetchAllOrganizations } from '../../slice/organizationsSlice';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';

// How many calendar months back the backend is asked for — generous enough
// to cover a full year plus some margin for the year/month filters below.
const MONTHS_BACK = 14;

const getAgentName = (item, t) => item.task_name || t('usageHistory.unknownAgent');

const emptyTotals = () => ({ runs: 0, questions: 0, tokens: 0, userUploadedPages: 0, standardFilePages: 0 });

const sumRows = (rows) =>
  rows.reduce((acc, r) => {
    acc.runs += r.runs || 0;
    acc.questions += r.questions || 0;
    acc.tokens += (r.tokens_input || 0) + (r.tokens_output || 0);
    acc.userUploadedPages += r.user_uploaded_pages || 0;
    acc.standardFilePages += r.standard_file_pages || 0;
    return acc;
  }, emptyTotals());

const groupByMonth = (rows) => {
  const map = {};
  rows.forEach((r) => {
    const key = `${r.year}-${String(r.month).padStart(2, '0')}`;
    if (!map[key]) map[key] = { year: r.year, month: r.month, key, rows: [] };
    map[key].rows.push(r);
  });
  return Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
};

/* ── Custom dropdown (unchanged pattern from before) ──────────────────── */
const CustomSelect = ({ icon: Icon, value, placeholder, options, onChange, aispaceColor, getLabel, getValue, disabled }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 180) });
    }
    setOpen((o) => !o);
  };

  const selected = options.find((o) => getValue(o) === value);
  const label = selected ? getLabel(selected) : null;

  return (
    <div className="relative" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className="flex items-center gap-2 px-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg bg-white hover:bg-[#F9FAFB] focus:outline-none transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        style={open ? { borderColor: aispaceColor } : {}}
      >
        {Icon && <Icon size={15} className="text-[#9CA3AF] flex-shrink-0" />}
        <span className={label ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{label || placeholder}</span>
        <ChevronDown size={15} className={`text-[#6B7280] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && menuPos && (
        <div
          className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-[300] max-h-[260px] overflow-y-auto py-1"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}
        >
          {options.map((opt) => {
            const isSel = getValue(opt) === value;
            return (
              <button
                key={getValue(opt)}
                type="button"
                onClick={() => { onChange(getValue(opt)); setOpen(false); }}
                className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSel ? 'bg-[#F3F4F6]' : ''}`}
              >
                <span>{getLabel(opt)}</span>
                {isSel && <Check size={15} style={{ color: aispaceColor }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Stat card ──────────────────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, sub, aispaceColor }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
    <div className="flex items-center gap-2.5 mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${aispaceColor}18` }}>
        <Icon size={15} style={{ color: aispaceColor }} />
      </div>
      <span className="text-[13px] text-[#6B7280]">{label}</span>
    </div>
    <div className="text-[26px] font-bold text-[#111827] leading-none">{value.toLocaleString()}</div>
    {sub && <div className="text-[12px] text-[#9CA3AF] mt-1.5">{sub}</div>}
  </div>
);

/* ── Historical month group ─────────────────────────────────────────── */
const MonthGroup = ({ group, aispaceColor, MONTHS, t }) => {
  const [open, setOpen] = useState(false);
  const totals = useMemo(() => sumRows(group.rows), [group.rows]);
  const monthLabel = `${MONTHS[group.month - 1]} ${group.year}`;
  const agentNames = [...new Set(group.rows.map((r) => getAgentName(r, t)))];

  return (
    <>
      <div
        className="flex items-center px-8 py-4 hover:bg-[#FAFAFA] transition-colors cursor-pointer border-b border-[#E5E7EB]"
        style={{ borderLeft: `3px solid ${open ? aispaceColor : 'transparent'}` }}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="w-[160px] flex-shrink-0 flex items-center gap-2">
          {open
            ? <ChevronDown size={14} style={{ color: aispaceColor }} />
            : <ChevronRight size={14} className="text-[#9CA3AF]" />}
          <span className="text-[14px] font-semibold text-[#111827]">{monthLabel}</span>
        </div>
        <div className="flex-1 min-w-0 pr-4">
          <span className="text-[13px] text-[#6B7280]">
            {agentNames.slice(0, 2).join(', ')}{agentNames.length > 2 && ' …'}
          </span>
        </div>
        <div className="w-[100px] flex-shrink-0 text-[14px] font-medium text-[#111827]">{totals.runs.toLocaleString()}</div>
        <div className="w-[110px] flex-shrink-0 text-[14px] text-[#374151]">{totals.questions.toLocaleString()}</div>
        <div className="w-[110px] flex-shrink-0 text-[14px] text-[#374151]">{totals.tokens.toLocaleString()}</div>
        <div className="w-[140px] flex-shrink-0 text-[14px] text-[#374151]">{totals.userUploadedPages.toLocaleString()}</div>
        <div className="w-[160px] flex-shrink-0 text-[14px] text-[#374151]">{totals.standardFilePages.toLocaleString()}</div>
      </div>

      {open && group.rows.map((item) => {
        const limitSet = item.standard_pages_per_month != null && item.standard_pages_per_month > 0;
        return (
          <div
            key={`${group.key}-${item.task_id}`}
            className="flex items-center px-8 py-3.5 bg-[#FAFAFA] border-b border-[#E5E7EB] hover:bg-[#F5F5F5] transition-colors"
            style={{ borderLeft: `3px solid ${aispaceColor}` }}
          >
            <div className="w-[160px] flex-shrink-0 pl-6" />
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-[13px] font-medium text-[#374151] truncate">{getAgentName(item, t)}</p>
            </div>
            <div className="w-[100px] flex-shrink-0 text-[13px] font-medium text-[#111827]">{(item.runs || 0).toLocaleString()}</div>
            <div className="w-[110px] flex-shrink-0 text-[13px] text-[#374151]">{(item.questions || 0).toLocaleString()}</div>
            <div className="w-[110px] flex-shrink-0 text-[13px] text-[#374151]">
              {((item.tokens_input || 0) + (item.tokens_output || 0)).toLocaleString()}
            </div>
            <div className="w-[140px] flex-shrink-0 text-[13px] text-[#374151]">{(item.user_uploaded_pages || 0).toLocaleString()}</div>
            <div className="w-[160px] flex-shrink-0 text-[13px] text-[#374151]">
              {(item.standard_file_pages || 0).toLocaleString()}
              {limitSet
                ? <span className="text-[#9CA3AF]"> / {item.standard_pages_per_month.toLocaleString()}</span>
                : <span className="text-[#9CA3AF]"> {t('usageHistory.unlimitedSuffix')}</span>}
            </div>
          </div>
        );
      })}
    </>
  );
};

/* ── Main ───────────────────────────────────────────────────────────── */
const UsageHistory = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { aispaceColor } = useThemeColor();
  const { organizations, error: organizationsError } = useSelector((s) => s.organizations);
  const [selectedOrg, setSelectedOrg]     = useState('');
  const [selectedYear, setSelectedYear]   = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [loading, setLoading]             = useState(false);
  const [usageHistory, setUsageHistory]   = useState([]);
  const { addToast, ToastContainer } = useToast();

  const MONTHS = t('usageHistory.months').split(',');

  useEffect(() => { dispatch(fetchAllOrganizations()); }, [dispatch]);

  const loadHistory = (orgId) => {
    if (!orgId) {
      setUsageHistory([]);
      return;
    }
    setLoading(true);
    axios.get(`${BASE_URL}/organizations/${orgId}/usage-history?months=${MONTHS_BACK}`, authHeaders())
      .then((res) => setUsageHistory(res.data?.usage_history || []))
      .catch(() => addToast(t('usageHistory.loadError'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadHistory(selectedOrg); setSelectedMonth(0); }, [selectedOrg]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSelectedMonth(0); }, [selectedYear]);

  // Open on the response's newest year, not the browser's — they differ around New Year.
  useEffect(() => {
    if (!usageHistory.length) return;
    setSelectedYear((cur) => (usageHistory.some((h) => h.year === cur)
      ? cur
      : usageHistory.reduce((max, h) => (h.year > max ? h.year : max), usageHistory[0].year)));
  }, [usageHistory]);

  const years = useMemo(() => {
    const set = new Set(usageHistory.map((h) => h.year));
    // Browser year only when the response is empty, so no unreachable year is listed.
    if (!set.size) set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [usageHistory]);

  const filtered = useMemo(() => {
    // Newest month comes from the payload; the browser clock disagrees with UTC at month end.
    const newest = usageHistory.reduce(
      (acc, h) => (!acc || h.year > acc.year || (h.year === acc.year && h.month > acc.month)
        ? { year: h.year, month: h.month } : acc),
      null
    );
    return usageHistory.filter((h) => {
      if (h.year !== selectedYear) return false;
      if (selectedMonth !== 0 && h.month !== selectedMonth) return false;
      // Empty rows hidden in past months only — a new agent must show before first use.
      if (newest && h.year === newest.year && h.month === newest.month) return true;
      return (h.runs || 0) + (h.questions || 0) + (h.user_uploaded_pages || 0) + (h.standard_file_pages || 0) > 0;
    });
  }, [usageHistory, selectedYear, selectedMonth]);

  const grouped = useMemo(() => groupByMonth(filtered), [filtered]);
  const totals = useMemo(() => sumRows(filtered), [filtered]);
  const hasData = grouped.length > 0;

  const COLS = [
    { label: t('usageHistory.colMonth'),         w: 'w-[160px] flex-shrink-0' },
    { label: t('usageHistory.colAgent'),         w: 'flex-1 min-w-0 pr-4'     },
    { label: t('usageHistory.colRuns'),          w: 'w-[100px] flex-shrink-0' },
    { label: t('usageHistory.colQuestions'),     w: 'w-[110px] flex-shrink-0' },
    { label: t('usageHistory.colTokens'),        w: 'w-[110px] flex-shrink-0' },
    { label: t('usageHistory.colUserPages'),     w: 'w-[140px] flex-shrink-0' },
    { label: t('usageHistory.colStandardPages'), w: 'w-[160px] flex-shrink-0' },
  ];

  const yearOptions  = years.map((y) => ({ value: y, label: String(y) }));
  const monthOptions = [
    { value: 0, label: t('usageHistory.allMonths') },
    ...MONTHS.map((name, i) => ({ value: i + 1, label: name })),
  ];

  return (
    <div>
      <ToastContainer />

      {organizationsError && (
        <div className="mb-4 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{organizationsError}</div>
      )}

      <div className="mb-6">
        <h1 className="text-[20px] font-semibold text-[#111827]">{t('usageHistory.title')}</h1>
        <p className="text-[13px] text-[#6B7280] mt-0.5">{t('usageHistory.subtitle')}</p>
      </div>

      {selectedOrg && !loading && hasData && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <StatCard icon={TrendingDown} label={t('usageHistory.cardRunsLabel')} value={totals.runs} aispaceColor={aispaceColor} />
          <StatCard icon={MessageSquare} label={t('usageHistory.cardQuestionsLabel')} value={totals.questions} aispaceColor={aispaceColor} />
          <StatCard icon={Zap} label={t('usageHistory.cardTokensLabel')} value={totals.tokens} sub={t('usageHistory.cardTokensSub')} aispaceColor={aispaceColor} />
          <StatCard icon={Upload} label={t('usageHistory.cardUserPagesLabel')} value={totals.userUploadedPages} aispaceColor={aispaceColor} />
          <StatCard icon={FileText} label={t('usageHistory.cardStandardPagesLabel')} value={totals.standardFilePages} aispaceColor={aispaceColor} />
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">

        <div className="flex items-center gap-3 px-8 py-4 border-b border-[#E5E7EB]">
          <CustomSelect
            icon={Building2}
            value={selectedOrg}
            placeholder={t('usageHistory.orgPlaceholder')}
            options={organizations.map((o) => ({ id: String(o.organization_id), name: o.organization_name }))}
            onChange={setSelectedOrg}
            aispaceColor={aispaceColor}
            getValue={(o) => o.id}
            getLabel={(o) => o.name}
          />
          {selectedOrg && (
            <CustomSelect
              icon={Calendar}
              value={selectedYear}
              placeholder=""
              options={yearOptions}
              onChange={(v) => setSelectedYear(Number(v))}
              aispaceColor={aispaceColor}
              getValue={(o) => o.value}
              getLabel={(o) => o.label}
            />
          )}
          {selectedOrg && (
            <CustomSelect
              value={selectedMonth}
              placeholder={t('usageHistory.allMonths')}
              options={monthOptions}
              onChange={(v) => setSelectedMonth(Number(v))}
              aispaceColor={aispaceColor}
              getValue={(o) => o.value}
              getLabel={(o) => o.label}
            />
          )}
          {selectedOrg && (
            <button
              onClick={() => loadHistory(selectedOrg)}
              className="ml-auto flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
            >
              <RefreshCw size={14} />
              {t('usageHistory.refresh')}
            </button>
          )}
        </div>

        {selectedOrg && !loading && hasData && (
          <div className="flex items-center px-8 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
            {COLS.map((col) => (
              <div key={col.label} className={`${col.w} text-[12px] font-medium text-[#6B7280] uppercase tracking-wider whitespace-nowrap`}>
                {col.label}
              </div>
            ))}
          </div>
        )}

        {!selectedOrg ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-8">
            <div className="w-14 h-14 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full flex items-center justify-center mb-4">
              <Building2 size={24} className="text-[#9CA3AF]" />
            </div>
            <p className="text-[15px] font-semibold text-[#111827]">{t('usageHistory.emptyOrgTitle')}</p>
            <p className="text-[13px] text-[#9CA3AF] mt-1 max-w-xs">{t('usageHistory.emptyOrgSubtitle')}</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-20">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}
            />
          </div>
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-8">
            <div className="w-14 h-14 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full flex items-center justify-center mb-4">
              <Calendar size={24} className="text-[#9CA3AF]" />
            </div>
            <p className="text-[15px] font-semibold text-[#111827]">
              {t('usageHistory.emptyDataTitle')} {selectedMonth !== 0 ? `${MONTHS[selectedMonth - 1]} ` : ''}{selectedYear}
            </p>
            <p className="text-[13px] text-[#9CA3AF] mt-1">{t('usageHistory.emptyDataSubtitle')}</p>
          </div>
        ) : (
          <div>
            {grouped.map((group) => (
              <MonthGroup key={group.key} group={group} aispaceColor={aispaceColor} MONTHS={MONTHS} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageHistory;
